import { useLingui } from '@lingui/react/macro';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';

import { applicationLinkValidator } from 'modules/application-link/domain/validators/application-link.validator';
import { PLAYER_POSITION_LABELS } from 'modules/teams/domain/constants/player-position-labels';
import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import { UDFormCheckbox } from 'ud-form/components';
import UDFormInput from 'ud-form/components/input';
import { SelectOption } from 'ud-form/components/select/component';
import UDFormSelect from 'ud-form/components/select/ud-form-select';
import UDText from 'ud-ui/components/text';
import PhotoField from './photo-field';
import S from './styles';

// Форматтер и парсер для полей даты рождения
const formatDate = (value: string) => {
  // Удаляем все нецифровые символы
  const numbers = value.replace(/\D/g, '');

  // Ограничиваем длину до 8 цифр (DDMMYYYY)
  const cleaned = numbers.slice(0, 8);

  // Форматируем с точками
  let formatted = '';
  for (let i = 0; i < cleaned.length; i++) {
    if (i === 2 || i === 4) {
      formatted += '.';
    }
    formatted += cleaned[i];
  }

  return formatted;
};

const parseDate = (value: string) => {
  // Удаляем все символы кроме цифр и точек
  const cleaned = value.replace(/[^0-9.]/g, '');

  // Ограничиваем длину до 10 символов (DD.MM.YYYY)
  return cleaned.slice(0, 10);
};

// Форматтер и парсер для поля игрового номера
const formatNumber = (value: string) => {
  // Оставляем только цифры, ограничиваем до 2 знаков (номер игрока 1-99)
  return value.replace(/\D/g, '').slice(0, 2);
};

const parseNumber = formatNumber;

export interface IFormValues {
  surname: string;
  name: string;
  patronymic: string;
  birthday: string;
  photo?: (File | string)[];
  number: string;
  position?: SelectOption<PlayerPosition> | null;
  consent: boolean;
}

interface IProps {
  onSubmit: (values: IFormValues, formikHelpers: FormikHelpers<IFormValues>) => void;
}

const ApplicationLinkForm = (props: IProps) => {
  const { onSubmit } = props;
  const { t } = useLingui();

  const positionOptions = Object.values(PlayerPosition).map((position) => ({
    value: position,
    label: t(PLAYER_POSITION_LABELS[position]),
  }));

  return (
    <Formik<IFormValues>
      initialValues={{
        surname: '',
        name: '',
        patronymic: '',
        birthday: '',
        photo: [],
        number: '',
        position: null,
        consent: false,
      }}
      validationSchema={applicationLinkValidator}
      onSubmit={onSubmit}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {(formikProps: FormikProps<IFormValues>) => {
        return (
          <Form
            onSubmit={formikProps.handleSubmit}
            className="flex flex-col justify-center gap-5"
            style={{ maxWidth: 390, width: '100%' }}
          >
            {/*
              Имена без форматтера намеренно: он вырезал символы прямо при вводе,
              и «Артём» превращался в «Артм», а «Анна-Мария» — в «АннаМария».
              Недопустимые символы теперь ловит схема и объясняет их текстом
              под полем, вместо того чтобы молча съедать нажатие.
            */}
            <UDFormInput name="surname" placeholder="Фамилия" errorVisible={true} />
            <UDFormInput name="name" placeholder="Имя" errorVisible={true} />
            <UDFormInput name="patronymic" placeholder="Отчество (не обязательно)" errorVisible={true} />
            <UDFormInput
              name="birthday"
              placeholder="Дата рождения"
              errorVisible={true}
              mask="__.__.____"
              formatter={formatDate}
              parser={parseDate}
            />
            <UDFormInput name="number" placeholder="Игровой номер" errorVisible={true} formatter={formatNumber} parser={parseNumber} />
            <UDFormSelect name="position" placeholder="Амплуа" options={positionOptions} errorVisible={true} />
            <PhotoField />
            <S.CheckboxContainer>
              <UDFormCheckbox
                size={20}
                name="consent"
                containerProps={{
                  style: {
                    width: 'unset',
                  },
                }}
                onChange={() => {
                  formikProps.validateField('consent');
                }}
              />
              <UDText type="subhead" style={{ fontWeight: 300, opacity: 0.8 }} color="white">
                Я даю согласие на обработку персональных данных
              </UDText>
            </S.CheckboxContainer>
            <div className="flex-center flex-col gap-4">
              <S.Button
                type="submit"
                variant="primary"
                style={{ width: '100%' }}
                // Загрузка шапки страницы отправку не блокирует: для заявки нужен
                // только токен из адреса, а не название турнира и логотип.
                disabled={!formikProps.isValid || !formikProps.dirty}
                loading={formikProps.isSubmitting}
              >
                Отправить заявку
              </S.Button>
              <UDText type="subhead" style={{ fontWeight: 400, textAlign: 'center' }} color="white">
                Ваша заявка будет направлена в кабинет организатора
              </UDText>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ApplicationLinkForm;
